<template>
  <div class="metric-card">
    <div class="flex items-center">
      <div class="flex-shrink-0">
        <div :class="[
          'w-8 h-8 rounded-md flex items-center justify-center',
          iconClasses
        ]">
          <component :is="iconComponent" class="w-5 h-5 text-white" />
        </div>
      </div>

      <div class="ml-5 w-0 flex-1">
        <dl>
          <dt class="metric-label">
            {{ title }}
          </dt>
          <dd class="metric-value">
            {{ formattedValue }}
          </dd>
        </dl>
      </div>
    </div>

    <!-- Изменение/доп. информация -->
    <div v-if="change !== undefined || changeLabel" class="mt-4">
      <div class="flex items-center">
        <div v-if="change !== undefined" :class="changeClasses">
          <component
            :is="changeIcon"
            class="w-4 h-4 mr-1"
            v-if="changeIcon"
          />
          {{ formattedChange }}
        </div>
        <div v-if="changeLabel" class="text-sm text-gray-500 ml-2">
          {{ changeLabel }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  value: {
    type: [Number, String],
    required: true
  },
  change: {
    type: Number,
    default: undefined
  },
  changeLabel: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: 'ChartBarIcon'
  },
  color: {
    type: String,
    default: 'blue'
  }
})

// Компонент иконки
const iconComponent = computed(() => {
  const icons = {
    UserGroupIcon,
    CurrencyDollarIcon,
    PhoneIcon,
    BuildingOfficeIcon,
    ChartBarIcon
  }
  return icons[props.icon] || ChartBarIcon
})

// Классы для цвета иконки
const iconClasses = computed(() => {
  const colors = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    gray: 'bg-gray-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500'
  }
  return colors[props.color] || colors.blue
})

// Форматированное значение
const formattedValue = computed(() => {
  if (typeof props.value === 'number') {
    return props.value.toLocaleString('ru-RU')
  }
  return props.value
})

// Форматированное изменение
const formattedChange = computed(() => {
  if (props.change === undefined) return ''

  const prefix = props.change > 0 ? '+' : ''
  return `${prefix}${props.change.toLocaleString('ru-RU')}`
})

// Классы для изменения
const changeClasses = computed(() => {
  if (props.change === undefined) return 'metric-change'

  if (props.change > 0) {
    return 'metric-change-positive'
  } else if (props.change < 0) {
    return 'metric-change-negative'
  }
  return 'metric-change'
})

// Иконка изменения
const changeIcon = computed(() => {
  if (props.change === undefined) return null

  if (props.change > 0) {
    return ArrowUpIcon
  } else if (props.change < 0) {
    return ArrowDownIcon
  }
  return null
})
</script>